import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { fetchErrors } from "../api";
import BackLink from "../components/BackLink";
import Placeholder, { Pending } from "../components/Placeholder";
import { useT } from "../i18n";
import { useSession } from "../session";

/**
 * AdminErrors lists server failures (ADR-0139): what a push announced, and
 * where a reference id a reader quotes from a red sentence is looked up —
 * from the phone, rather than from psql.
 */
export default function AdminErrors() {
  const { me, isPending } = useSession();
  const t = useT();
  const isAdmin = !!me?.user.platform_admin;
  const errors = useQuery({
    queryKey: ["admin", "errors"],
    queryFn: fetchErrors,
    enabled: isAdmin,
  });

  if (!isPending && !isAdmin) return <Navigate to="/" replace />;

  const items = errors.data?.items ?? [];

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <BackLink to="/admin">{t.admin.back}</BackLink>
      <header className="mt-4 mb-8">
        <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight">
          {t.errors.title}
        </h1>
        <p className="mt-1 text-sm text-muted">{t.errors.what}</p>
      </header>

      {(isPending || errors.isPending) && (
        <Pending label={t.common.loading}>
          <Placeholder className="h-40 rounded-card" />
        </Pending>
      )}

      {errors.isError && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {t.errors.loadFailed}{" "}
          <button type="button" onClick={() => void errors.refetch()} className="underline">
            {t.common.tryAgain}
          </button>
        </p>
      )}

      {errors.data && items.length === 0 && (
        <p data-testid="errors-empty" className="text-sm text-muted">
          {t.errors.empty}
        </p>
      )}

      <ul data-testid="errors-list" className="space-y-3">
        {items.map((e) => (
          <li
            key={e.request_id + e.at}
            data-testid="error-row"
            className="rounded-card border border-line bg-surface p-3 text-sm"
          >
            {/* Routes, ids and Go errors are Latin: isolated so a Persian
                interface does not reorder their slashes and colons. */}
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-semibold tabular-nums">{e.status}</span>
              <bdi className="font-mono text-xs">
                {e.method} {e.route}
              </bdi>
              <span className="text-xs text-muted">{new Date(e.at).toLocaleString()}</span>
            </div>
            <bdi data-testid="error-request-id" className="mt-1 block font-mono text-xs text-muted">
              {e.request_id}
            </bdi>
            <pre dir="ltr" className="mt-2 max-h-40 overflow-auto text-xs whitespace-pre-wrap">
              {e.error}
            </pre>
            {e.user_id && (
              <p className="mt-1 text-xs text-muted">
                {t.errors.user}: <bdi className="font-mono">{e.user_id}</bdi>
              </p>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
